import { useEffect, useState } from "react";
import { Form, Image, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { authSelector, selectedOrganizationSelector } from "../../reducers";
import { Confirmation } from "../../common/confirmation";
import {
  organizationPropertyDelete,
  organizationPropertyGet,
  organizationPropertySet,
  organizationSettingsEsalaryGet,
  organizationSettingsEsalarySet,
  organizationTestToken,
  statementTrialTokensGet,
} from "../../api";
import { getIntegrations } from "../../actions/organization/activeintegrations";
import { Button } from "../../common/Button.tsx";
import { EventMapping } from "./modals/SettingsProcountor/EventMapping/EventMapping.jsx";
import { EventMappingSave } from "./modals/SettingsProcountor/EventMapping/EventMappingSave.jsx";
import { SettingsProcountorContextProvider } from "./modals/SettingsProcountor/SettingsProcountorContext.jsx";
import Switch from "../../common/Switch";

const ACTIVATION_KEY = "settingsEsalary";

export function SettingsEsalaryModal({ setShow, active }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);
  const businessId = selectedOrganization.businessIdentifier ?? auth.companyBusinessId;

  const [activeTab, setActiveTab] = useState("general");

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      salaryUpdate: true,
      endEmployment: false,
    },
  });

  const salaryUpdate = "salaryUpdate";
  const endEmployment = "endEmployment";
  const closeModal = setShow;

  useEffect(() => {
    if (active) {
      fetchSettings();
    }
  }, [active, businessId]);

  async function fetchSettings() {
    const res = await organizationSettingsEsalaryGet(businessId);
    if (res?.status === 200) {
      setValue(salaryUpdate, res.data[salaryUpdate]);
      setValue(endEmployment, res.data[endEmployment]);
    }
  }

  const handleToggleActivation = async () => {
    if (!active) {
      await organizationPropertySet(businessId, ACTIVATION_KEY, "true");
    } else {
      await organizationPropertyDelete(businessId, ACTIVATION_KEY);
    }
    dispatch(getIntegrations(businessId));
  };

  const onSubmit = async (data) => {
    if (!active) return;
    await organizationSettingsEsalarySet(businessId, data);
    closeModal();
  };

  return (
    <Modal show onHide={closeModal} size="xl">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ lineHeight: 2 }}>
            <Image src="/integrations/heeros-epalkat.svg" height={25} alt="finago ePalkat" />{" "}
            {t("Settings", "Asetukset").toLowerCase()}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div style={{ opacity: active ? 1 : 0.5, pointerEvents: active ? "auto" : "none" }}>
            <Tabs activeKey={activeTab} onSelect={(tab) => setActiveTab(tab)} className="mb-3">
              <Tab eventKey="general" title={t("General", "Yleistä")}>
                <Switch
                  id={salaryUpdate}
                  name={salaryUpdate}
                  control={control}
                  className="mb-1"
                  label="Palkkatietojen päivitys HR:stä ePalkkoihin tallennettaessa henkilötietoja"
                />
                <Switch
                  id={endEmployment}
                  name={endEmployment}
                  control={control}
                  className="mb-1"
                  label="Päätettäessä työsuhde se päätetään myös ePalkkoihin"
                />
              </Tab>
              <Tab eventKey="eventMapping" title={t("EventMapping", "Tapahtumien kohdistus")}>
                <EventMapping integration="ESALARY" />
              </Tab>
            </Tabs>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Confirmation
            title={
              active
                ? t("DeactivateConfirm", "Poistetaanko integraatio käytöstä?")
                : t("ActivateConfirm", "Otetaanko integraatio käyttöön?")
            }
            actionFunction={handleToggleActivation}
          >
            <Button variant={active ? "warning" : "success"}>
              {active ? t("Deactivate", "Poista käytöstä") : t("Activate", "Ota käyttöön")}
            </Button>
          </Confirmation>

          {active && (
            <>
              {activeTab === "general" && (
                <Button type="submit" variant="primary">
                  {t("Save", "Tallenna")}
                </Button>
              )}
              {activeTab === "eventMapping" && <EventMappingSave closeModal={closeModal} />}
            </>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export const SettingsEsalary = ({ setShow, active }) => (
  <SettingsProcountorContextProvider>
    <SettingsEsalaryModal setShow={setShow} active={active} />
  </SettingsProcountorContextProvider>
);


export function SettingsPsa({ setShow, active }) {
  const key = "tokenPsa";

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);

  const { register, handleSubmit, setValue, watch } = useForm();

  const [testResult, setTestResult] = useState(<br />); // reserve space for test result

  async function fetchToken() {
    const res = await organizationPropertyGet(selectedOrganization.businessIdentifier ?? auth.companyBusinessId, key);
    if (res && res.status === 200) {
      setValue(key, res.data);
    }
  }

  async function setToken(data) {
    const res = await organizationPropertySet(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
      data[key],
    );
    if (res && res.status === 200) {
      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function deleteToken() {
    setTestResult(<br />);
    const res = await organizationPropertyDelete(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
    );
    if (res && res.status === 200) {
      setValue(key, "");

      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function testToken() {
    setTestResult(<Spinner size="sm" variant="primary" />);
    const res = await organizationTestToken("psa");
    if (res && res.status === 200) {
      switch (res.data) {
        case true:
          setTestResult(`${t("TokenValid", "Token is valid")} ✅`);
          break;
        case false:
          setTestResult(`${t("TokenInvalid", "Token is invalid")} ⛔`);
          break;
        default:
          setTestResult(`${t("ApiError", "API error")} ⚡`);
      }
    }
  }

  useEffect(() => {
    if (active) {
      fetchToken();
    }
  }, [active]);

  const closeModal = () => setShow();

  const tokenValue = watch(key);

  return (
    <Modal show onHide={closeModal}>
      <Form onSubmit={handleSubmit(setToken)} /* form must be inside modal to handle submit */>
        <Modal.Header closeButton>
          <Modal.Title style={{ lineHeight: 2 }}>
            <Image src="/integrations/heeros-psa.svg" height={25} alt="finago PSA" /> {t("integration", "integraatio")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!active && <p>{t("ApiEnter", "Syötä API-avain alla olevaan kenttään ja aktivoi integraatio.")}</p>}
          <Form.Group className="mb-3" controlId={key}>
            <Form.Label>PSA Token</Form.Label>
            <Form.Control
              {...register(key)}
              readOnly={active}
              onFocus={active ? (e) => e.target.select() : null}
              style={{ userSelect: active ? "all" : "initial" }}
            />
          </Form.Group>
          {testResult}
        </Modal.Body>

        <Modal.Footer>
          {active && <Button onClick={testToken}>{t("TestConnection", "Testaa yhteys")}</Button>}
          <Confirmation
            title={
              active
                ? t(
                  "NoteDeactivate",
                  "API-avain poistetaan ja pyynnöt rajapintaan vanhalla avaimella ei enää toimi. Haluatko poistaa integraation käytöstä",
                )
                : t("NoteActivate", "Haluatko määrittää uuden API-avaimen")
            }
            actionFunction={active ? deleteToken : handleSubmit(setToken)}
          >
            <Button
              key="key"
              variant={active ? "warning" : "success"}
              disabled={!tokenValue || tokenValue.trim().length < 1}
            >
              {active ? t("Deactivate", "Poista käytöstä") : t("Activate", "Ota käyttöön")}
            </Button>
          </Confirmation>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export function SettingsSevera({ setShow, active }) {
  const key = "tokenSevera";

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);

  const { register, handleSubmit, setValue, watch } = useForm();

  const [testResult, setTestResult] = useState(<br />); // reserve space for test result

  // restore settings from JSON string
  async function fetchToken() {
    const res = await organizationPropertyGet(selectedOrganization.businessIdentifier ?? auth.companyBusinessId, key);
    if (res && res.status === 200) {
      for (const key in res.data) {
        setValue(key, res.data[key]);
      }
    }
  }

  // save settings as JSON string to organization's key-value store
  async function setToken(data) {
    const res = await organizationPropertySet(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
      JSON.stringify(data),
    );

    if (res && res.status === 200) {
      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function deleteToken() {
    setTestResult(<br />);
    const res = await organizationPropertyDelete(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
    );
    if (res && res.status === 200) {
      for (const key in formValues) {
        setValue(key, "");
      }

      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function testToken() {
    setTestResult(<Spinner size="sm" variant="primary" />);
    const res = await organizationTestToken("severa");
    if (res && res.status === 200) {
      switch (res.data) {
        case true:
          setTestResult(`${t("TokenValid", "Token is valid")} ✅`);
          break;
        case false:
          setTestResult(`${t("TokenInvalid", "Token is invalid")} ⛔`);
          break;
        default:
          setTestResult(`${t("ApiError", "API error")} ⚡`);
      }
    }
  }

  useEffect(() => {
    if (active) {
      fetchToken();
    }
  }, [active]);

  const closeModal = () => setShow();

  const formValues = watch();

  return (
    <Modal show onHide={closeModal}>
      <Form onSubmit={handleSubmit(setToken)} /* form must be inside modal to handle submit */>
        <Modal.Header closeButton>
          <Modal.Title style={{ lineHeight: 2 }}>
            <Image src="/integrations/visma-severa.svg" height={25} alt="severa" /> {t("integration", "integraatio")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!active && (
            <p>
              <a
                href="https://severa.visma.com/home?pType=20&pAlign=RIGHT&pAnchor=restApiSection"
                target="_blank"
                rel="noreferrer"
              >
                {t("ApiEnterInfo", "Syötä tarvittavat API-tiedot alla oleviin kenttiin ja aktivoi integraatio.")}
              </a>
            </p>
          )}
          <Form.Group className="mb-3" controlId="client_id">
            <Form.Label>Client ID</Form.Label>
            <Form.Control
              {...register("client_id")}
              readOnly={active}
              onFocus={active ? (e) => e.target.select() : null}
              style={{ userSelect: active ? "all" : "initial" }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="client_secret">
            <Form.Label>Client secret</Form.Label>
            <Form.Control
              {...register("client_secret")}
              readOnly={active}
              onFocus={active ? (e) => e.target.select() : null}
              style={{ userSelect: active ? "all" : "initial" }}
            />
          </Form.Group>
          {testResult}
        </Modal.Body>

        <Modal.Footer>
          {active && <Button onClick={testToken}>{t("TestConnection", "Testaa yhteys")}</Button>}
          <Confirmation
            title={
              active
                ? t(
                  "NoteDeactivate",
                  "API-avain poistetaan ja pyynnöt rajapintaan vanhalla avaimella ei enää toimi. Haluatko poistaa integraation käytöstä",
                )
                : t("NoteActivate", "Haluatko määrittää uuden API-avaimen")
            }
            actionFunction={active ? deleteToken : handleSubmit(setToken)}
          >
            <Button
              key="key"
              variant={active ? "warning" : "success"}
              disabled={
                formValues?.client_id === undefined ||
                formValues.client_id?.trim().length < 1 ||
                formValues.client_secret?.trim().length < 1
              }
            >
              {active ? t("Deactivate", "Poista käytöstä") : t("Activate", "Ota käyttöön")}
            </Button>
          </Confirmation>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export function SettingsNetvisor({ setShow, active }) {
  const key = "tokenNetvisor";

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);

  const { register, handleSubmit, setValue, watch } = useForm();

  const [testResult, setTestResult] = useState(<br />); // reserve space for test result

  // restore settings from JSON string
  async function fetchToken() {
    const res = await organizationPropertyGet(selectedOrganization.businessIdentifier ?? auth.companyBusinessId, key);
    if (res && res.status === 200) {
      for (const key in res.data) {
        setValue(key, res.data[key]);
      }
    }
  }

  // save settings as JSON string to organization's key-value store
  async function setToken(data) {
    const res = await organizationPropertySet(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
      JSON.stringify(data),
    );

    if (res && res.status === 200) {
      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function deleteToken() {
    setTestResult(<br />);
    const res = await organizationPropertyDelete(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
    );
    if (res && res.status === 200) {
      for (const key in formValues) {
        setValue(key, "");
      }

      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function testToken() {
    setTestResult(<Spinner size="sm" variant="primary" />);
    const res = await organizationTestToken("netvisor");
    if (res && res.status === 200) {
      if (!res.data) setTestResult(`${t("TokenInvalid", "Token is invalid")} ⛔`);
      else {
        if (res.data.Allowed) {
          const result = [];
          Object.keys(res.data).map(key => {
            result.push(<b>{key}</b>);
            const items = [];
            res.data[key].forEach(item => items.push(<>{item.uri}<br /></>));
            let color = "initial";
            if (key === "Allowed") color = "darkgreen";
            else if (key === "Forbidden") color = "red";
            result.push(<div style={{ color, marginBottom: "1em" }}>{items}</div>);
          });
          setTestResult(<div><p>{`${t("TokenValid", "Token is valid")} ✅`}</p>{result}</div>);
        }
        else setTestResult(`${t("ApiError", "API error")} ⚡`);
      }
    }
  }

  useEffect(() => {
    if (active) {
      fetchToken();
    }
  }, [active]);

  const closeModal = () => setShow();

  const formValues = watch();

  return (
    <Modal show onHide={closeModal}>
      <Form onSubmit={handleSubmit(setToken)} /* form must be inside modal to handle submit */>
        <Modal.Header closeButton>
          <Modal.Title style={{ lineHeight: 2 }}>
            <Image src="/integrations/visma-netvisor.svg" height={25} alt="netvisor by VISMA" />{" "}
            {t("integration", "integraatio")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!active && (
            <>
              <p>
                <a
                  href="https://support.netvisor.fi/fi/support/solutions/articles/77000498457-rajapintaresurssien-k%C3%A4ytt%C3%B6oikeuksien-salliminen-ja-poistaminen#r%C3%A4%C3%A4t%C3%A4l%C3%B6idyt%20integraatiot"
                  target="_blank"
                  rel="noreferrer"
                >
                  Ohjeita
                </a>
              </p>
              <p>
                <a
                  href="https://koulutus.netvisor.fi/evo/admin/apisecuritypanel/apisecuritypanel.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  Valitse Netvisor-käyttöliittymästä räätälöidyt integraatiot
                </a>{" "}
                ja Hae integraatiota käyttöoikeusavaimella <i>706C532D26370A96FDE58BD82DB04111</i>
              </p>
              <p>
                <a
                  href="https://koulutus.netvisor.fi/evo/admin/webserviceintegration/webserviceintegrationusers.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  Tämän jälkeen syötä tarvittavat Ohjelmistorajapintatunnukset alla oleviin kenttiin ja aktivoi
                  integraatio.
                </a>
              </p>
              <p>Finago HR easy sekä Netvisor -yritystiedoissa oleva Y-tunnus tulee olla sama molemmissa.</p>
            </>
          )}
          <Form.Group className="mb-3" controlId="customerId">
            <Form.Label>Käyttäjätunniste</Form.Label>
            <Form.Control
              {...register("customerId")}
              readOnly={active}
              onFocus={active ? (e) => e.target.select() : null}
              style={{ userSelect: active ? "all" : "initial" }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="customerKey">
            <Form.Label>Avain</Form.Label>
            <Form.Control
              {...register("customerKey")}
              readOnly={active}
              onFocus={active ? (e) => e.target.select() : null}
              style={{ userSelect: active ? "all" : "initial" }}
            />
          </Form.Group>
          {testResult}
        </Modal.Body>

        <Modal.Footer>
          {active && <Button onClick={testToken}>{t("TestConnection", "Testaa yhteys")}</Button>}
          <Confirmation
            title={
              active
                ? t(
                  "NoteDeactivate",
                  "API-avain poistetaan ja pyynnöt rajapintaan vanhalla avaimella ei enää toimi. Haluatko poistaa integraation käytöstä",
                )
                : t("NoteActivate", "Haluatko määrittää uuden API-avaimen")
            }
            actionFunction={active ? deleteToken : handleSubmit(setToken)}
          >
            <Button
              key="key"
              variant={active ? "warning" : "success"}
              disabled={
                !active &&
                (formValues?.customerId === undefined ||
                  formValues.customerId?.trim().length < 1 ||
                  formValues.customerKey?.trim().length < 1)
              }
            >
              {active ? t("Deactivate", "Poista käytöstä") : t("Activate", "Ota käyttöön")}
            </Button>
          </Confirmation>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export function SettingsOpenAi({ setShow, active }) {
  const key = "tokenOpenAi";

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const auth = useSelector(authSelector);
  const selectedOrganization = useSelector(selectedOrganizationSelector);

  const { register, handleSubmit, setValue, watch } = useForm();

  const [testResult, setTestResult] = useState(<br />); // reserve space for test result

  const [trialTokens, setTrialTokens] = useState(0); // used trial tokens

  // restore settings from JSON string
  async function fetchToken() {
    const res = await organizationPropertyGet(selectedOrganization.businessIdentifier ?? auth.companyBusinessId, key);
    if (res && res.status === 200) {
      setValue(key, res.data);
    }
  }

  async function fetchTrialTokens() {
    setTrialTokens(await statementTrialTokensGet());
  }

  // save settings as JSON string to organization's key-value store
  async function setToken(data) {
    const res = await organizationPropertySet(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
      data[key],
    );

    if (res && res.status === 200) {
      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function deleteToken() {
    setTestResult(<br />);
    const res = await organizationPropertyDelete(
      selectedOrganization.businessIdentifier ?? auth.companyBusinessId,
      key,
    );
    if (res && res.status === 200) {
      setValue(key, "");

      if (auth.companyBusinessId) {
        dispatch(getIntegrations(selectedOrganization.businessIdentifier ?? auth.companyBusinessId)); // update parent (updates active)
      } else {
        console.error("companyBusinessId null for", auth.companyNumber);
      }
    }
  }

  async function testToken() {
    setTestResult(<Spinner size="sm" variant="primary" />);
    const res = await organizationTestToken("openai");
    if (res && res.status === 200) {
      switch (res.data) {
        case true:
          setTestResult(`${t("TokenValid", "Token is valid")} ✅`);
          break;
        case false:
          setTestResult(`${t("TokenInvalid", "Token is invalid")} ⛔`);
          break;
        default:
          setTestResult(`${t("ApiError", "API error")} ⚡`);
      }
    }
  }

  useEffect(() => {
    if (active) {
      fetchToken();
    } else {
      fetchTrialTokens();
    }
  }, [active]);

  const closeModal = () => setShow();

  const apiKey = watch(key);

  return (
    <Modal show onHide={closeModal}>
      <Form onSubmit={handleSubmit(setToken)} /* form must be inside modal to handle submit */>
        <Modal.Header closeButton>
          <Modal.Title style={{ lineHeight: 2 }}>
            <Image src="/integrations/openai.svg" height={25} alt="OpenAI" /> {t("integration", "integraatio")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!active && (
            <>
              <p>
                <a target="_blank" href="https://platform.openai.com/settings/organization/api-keys" rel="noreferrer">
                  {t("ApiEnterInfo", "Syötä tarvittavat API-tiedot alla oleviin kenttiin ja aktivoi integraatio.")}
                </a>
              </p>
              <p>Testitokeneita käytetty: {trialTokens}</p>
            </>
          )}
          <Form.Group className="mb-3" controlId={key}>
            <Form.Label>API Key</Form.Label>
            <Form.Control
              {...register(key)}
              readOnly={active}
              onFocus={active ? (e) => e.target.select() : null}
              style={{ userSelect: active ? "all" : "initial" }}
            />
          </Form.Group>
          {testResult}
        </Modal.Body>

        <Modal.Footer>
          {active && <Button onClick={testToken}>{t("TestConnection", "Testaa yhteys")}</Button>}
          <Confirmation
            title={
              active
                ? t(
                  "NoteDeactivate",
                  "API-avain poistetaan ja pyynnöt rajapintaan vanhalla avaimella ei enää toimi. Haluatko poistaa integraation käytöstä",
                )
                : t("NoteActivate", "Haluatko määrittää uuden API-avaimen")
            }
            actionFunction={active ? deleteToken : handleSubmit(setToken)}
          >
            <Button
              key="key"
              variant={active ? "warning" : "success"}
              disabled={apiKey === undefined || apiKey?.trim().length < 1}
            >
              {active ? t("Deactivate", "Poista käytöstä") : t("Activate", "Ota käyttöön")}
            </Button>
          </Confirmation>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
